import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, X, ArrowRight, ShoppingBag } from 'lucide-react';
import { getProducts } from '@/lib/api';
import { useCartStore } from '@/stores/cartStore';
import type { Product } from '@/types';

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function SearchModal({ isOpen, onClose }: SearchModalProps) {
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const addItem = useCartStore((s) => s.addItem);

  const { data: products = [], isLoading } = useQuery<Product[]>({
    queryKey: ['products'],
    queryFn: () => getProducts(),
    enabled: isOpen,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, onClose]);

  const filtered = products.filter((p) => {
    const q = query.trim().toLowerCase();
    if (!q) return false;
    return (
      p.name.toLowerCase().includes(q) ||
      p.tags.some((t) => t.toLowerCase().includes(q)) ||
      p.category.toLowerCase().includes(q) ||
      p.dose.toLowerCase().includes(q)
    );
  });

  const handleSelect = (slug: string) => {
    onClose();
    navigate(`/products/${slug}`);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-[90] bg-rp-ink/50 backdrop-blur-sm"
          />
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed left-4 right-4 top-[15vh] z-[100] mx-auto max-w-2xl overflow-hidden rounded-3xl border border-rp-line bg-rp-paper shadow-panel md:left-8 md:right-8"
          >
            <div className="flex items-center gap-3 border-b border-rp-line px-4 py-3 md:px-6">
              <Search size={20} className="text-rp-moss" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search products, tags, or doses…"
                className="flex-1 bg-transparent text-base text-rp-ink outline-none placeholder:text-rp-moss/60"
              />
              <button
                onClick={onClose}
                className="flex h-8 w-8 items-center justify-center rounded-full text-rp-moss hover:bg-rp-bone"
                aria-label="Close search"
              >
                <X size={16} />
              </button>
            </div>

            <div className="max-h-[55vh] overflow-y-auto p-2 md:p-4">
              {!query.trim() && (
                <div className="py-8 text-center text-sm text-rp-moss">
                  Type to search the AURA catalogue
                </div>
              )}

              {query.trim() && isLoading && (
                <div className="py-8 text-center text-sm text-rp-moss">Loading products…</div>
              )}

              {query.trim() && !isLoading && filtered.length === 0 && (
                <div className="py-8 text-center text-sm text-rp-moss">
                  No products match “{query}”
                </div>
              )}

              {filtered.map((product) => (
                <div
                  key={product.id}
                  className="group flex items-center gap-4 rounded-2xl p-3 transition-colors hover:bg-rp-bone"
                >
                  <button
                    onClick={() => handleSelect(product.slug)}
                    className="flex flex-1 items-center gap-4 text-left"
                  >
                    <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-rp-line bg-rp-bone">
                      {product.image ? (
                        <img
                          src={product.image}
                          alt={product.name}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <span className="font-mono text-xs font-bold text-rp-forest">AU</span>
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-rp-ink group-hover:text-rp-forest">
                        {product.name}
                      </p>
                      <p className="text-xs text-rp-moss">{product.dose}</p>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {product.tags.slice(0, 2).map((tag) => (
                          <span
                            key={tag}
                            className="rounded-full border border-rp-line-2 bg-rp-paper px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-rp-moss"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                    <ArrowRight
                      size={16}
                      className="text-rp-moss opacity-0 transition-opacity group-hover:opacity-100"
                    />
                  </button>
                  <button
                    onClick={() => addItem(product.id)}
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-rp-forest text-rp-paper hover:bg-rp-forest-2"
                    aria-label={`Add ${product.name} to cart`}
                  >
                    <ShoppingBag size={14} />
                  </button>
                </div>
              ))}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
